import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, Checkbox, FormControlLabel, Divider, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listFlowRoles } from '../../api/aprobari'
import {
  createNotificationRule, updateNotificationRule, DOWNTIME_CAUSES, downtimeEventType,
  type NotificationRule, type SaveNotificationRulePayload,
} from '../../api/reguliNotificare'
import { errorMessage } from '../../api/client'

const CAUSE_LABEL: Record<string, string> = {
  DefectiuneMecanica: 'Defecțiune mecanică', LipsaMaterial: 'Lipsă material', SchimbMatrita: 'Schimb matriță',
  Pauza: 'Pauză', Curatare: 'Curățare', LipsaUtilitate: 'Lipsă utilitate', Altele: 'Altele',
}
const EVENT_OPTIONS = DOWNTIME_CAUSES.map((c) => ({ value: downtimeEventType(c), label: `Oprire producție — ${CAUSE_LABEL[c]}` }))

const schema = z.object({
  eventType: z.string().min(1, 'Selectați tipul de eveniment'),
  roleId: z.string().min(1, 'Selectați rolul destinatar'),
  notifyInApp: z.boolean(),
  notifyEmail: z.boolean(),
  escalationThresholdMinutes: z.string().optional(),
  escalationRoleId: z.string(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { eventType: '', roleId: '', notifyInApp: true, notifyEmail: false, escalationThresholdMinutes: '', escalationRoleId: '' }

export default function NotificationRuleFormDialog({ target, onClose }: { target: NotificationRule | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: roles } = useQuery({ queryKey: ['administrare', 'fluxuri-aprobare', 'roluri'], queryFn: listFlowRoles, enabled: open })
  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (editing) {
      reset({
        eventType: target.eventType, roleId: target.roleId, notifyInApp: target.notifyInApp, notifyEmail: target.notifyEmail,
        escalationThresholdMinutes: target.escalationThresholdMinutes != null ? String(target.escalationThresholdMinutes) : '',
        escalationRoleId: target.escalationRoleId ?? '',
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveNotificationRulePayload = {
        eventType: values.eventType, roleId: values.roleId, notifyInApp: values.notifyInApp, notifyEmail: values.notifyEmail,
        escalationThresholdMinutes: values.escalationThresholdMinutes ? Number(values.escalationThresholdMinutes) : null,
        escalationRoleId: values.escalationRoleId || null,
      }
      if (editing) return updateNotificationRule(target.id, payload)
      return createNotificationRule(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['administrare', 'reguli-notificare'] })
      onClose()
    },
  })

  const escalationThreshold = watch('escalationThresholdMinutes')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare regulă de notificare' : 'Regulă de notificare nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="eventType" control={control} render={({ field }) => (
              <TextField {...field} select label="Tip eveniment" fullWidth error={!!errors.eventType} helperText={errors.eventType?.message}>
                {EVENT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )} />
            <Controller name="roleId" control={control} render={({ field }) => (
              <TextField {...field} select label="Rol destinatar (rutare inițială)" fullWidth error={!!errors.roleId} helperText={errors.roleId?.message}>
                {roles?.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
              </TextField>
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="notifyInApp" control={control} render={({ field }) => (
                <FormControlLabel control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Canal in-app" />
              )} />
              <Controller name="notifyEmail" control={control} render={({ field }) => (
                <FormControlLabel control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Canal email" />
              )} />
            </Stack>

            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Escaladare (opțional, FR-OPE-033)</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <Controller name="escalationThresholdMinutes" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Prag escaladare (minute)" fullWidth />
              )} />
              <Controller name="escalationRoleId" control={control} render={({ field }) => (
                <TextField {...field} select label="Rol de escaladare" fullWidth disabled={!escalationThreshold}>
                  <MenuItem value="">—</MenuItem>
                  {roles?.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                </TextField>
              )} />
            </Stack>

            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
